/**
 * Character Creation Wizard
 *
 * Multi-step wizard for creating player characters.
 * 5 steps: Basics -> Class -> Abilities -> Combat -> Review
 */

import { useState, useCallback } from 'react';
import { Icon, type IconName } from './Icon';
import { useCharacterStore } from '../stores/characterStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useDiceStore } from '../stores/diceStore';
import { CharacterImportModal } from './CharacterImportModal';
import type { AbilityScores, MagicLevel, TechLevel } from '@machina/core';
import { getModifier } from '@machina/core';

interface CharacterCreationWizardProps {
  onClose: () => void;
  onCreated?: (characterId: string) => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

// =============================================================================
// STATIC DATA
// =============================================================================

// Race data with setting restrictions
interface RaceData {
  name: string;
  minTech?: TechLevel; // Requires at least this tech level
  minMagic?: MagicLevel; // Requires at least this magic level
}

const ALL_RACES: RaceData[] = [
  { name: 'Human' },
  { name: 'Elf' },
  { name: 'Dwarf' },
  { name: 'Halfling' },
  { name: 'Half-Elf' },
  { name: 'Half-Orc' },
  { name: 'Gnome' },
  { name: 'Tiefling', minMagic: 'medium' }, // Requires magic for planar ancestry
  { name: 'Dragonborn', minMagic: 'medium' },
  { name: 'Aasimar', minMagic: 'medium' },
  { name: 'Goliath' },
  { name: 'Warforged', minTech: 'industrial' }, // Requires tech for constructs
  { name: 'Genasi', minMagic: 'wild' },
  { name: 'Changeling', minMagic: 'medium' },
];

// Class data with setting restrictions
interface ClassData {
  name: string;
  hitDie: number;
  icon: IconName;
  saves: string[];
  minMagic?: MagicLevel;
  minTech?: TechLevel;
}

const ALL_CLASSES: ClassData[] = [
  { name: 'Barbarian', hitDie: 12, icon: 'attack', saves: ['strength', 'constitution'] },
  { name: 'Bard', hitDie: 8, icon: 'horn', saves: ['dexterity', 'charisma'] },
  { name: 'Cleric', hitDie: 8, icon: 'sparkle', saves: ['wisdom', 'charisma'] },
  { name: 'Druid', hitDie: 8, icon: 'heart', saves: ['intelligence', 'wisdom'] },
  { name: 'Fighter', hitDie: 10, icon: 'sword', saves: ['strength', 'constitution'] },
  { name: 'Monk', hitDie: 8, icon: 'star', saves: ['strength', 'dexterity'] },
  { name: 'Paladin', hitDie: 10, icon: 'shield', saves: ['wisdom', 'charisma'] },
  { name: 'Ranger', hitDie: 10, icon: 'target', saves: ['strength', 'dexterity'] },
  { name: 'Rogue', hitDie: 8, icon: 'skull', saves: ['dexterity', 'intelligence'] },
  { name: 'Sorcerer', hitDie: 6, icon: 'crystal', saves: ['constitution', 'charisma'], minMagic: 'medium' },
  { name: 'Warlock', hitDie: 8, icon: 'moon', saves: ['wisdom', 'charisma'], minMagic: 'medium' },
  { name: 'Wizard', hitDie: 6, icon: 'wand', saves: ['intelligence', 'wisdom'], minMagic: 'medium' },
  { name: 'Artificer', hitDie: 8, icon: 'sparkle', saves: ['constitution', 'intelligence'], minTech: 'renaissance', minMagic: 'medium' },
];

// Tech/Magic level ordering for filtering
const TECH_ORDER: TechLevel[] = ['bronze', 'medieval', 'renaissance', 'industrial', 'steampunk'];
const MAGIC_ORDER: MagicLevel[] = ['low', 'medium', 'wild'];

function techMeetsMin(current: TechLevel, min?: TechLevel): boolean {
  if (!min) return true;
  return TECH_ORDER.indexOf(current) >= TECH_ORDER.indexOf(min);
}

function magicMeetsMin(current: MagicLevel, min?: MagicLevel): boolean {
  if (!min) return true;
  return MAGIC_ORDER.indexOf(current) >= MAGIC_ORDER.indexOf(min);
}

// Filter functions based on campaign profile
function getAvailableRaces(techLevel: TechLevel, magicLevel: MagicLevel): RaceData[] {
  return ALL_RACES.filter(r =>
    techMeetsMin(techLevel, r.minTech) && magicMeetsMin(magicLevel, r.minMagic)
  );
}

function getAvailableClasses(techLevel: TechLevel, magicLevel: MagicLevel): ClassData[] {
  return ALL_CLASSES.filter(c =>
    techMeetsMin(techLevel, c.minTech) && magicMeetsMin(magicLevel, c.minMagic)
  );
}

const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8];
const ABILITIES = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const;
type Ability = typeof ABILITIES[number];

const ABILITY_LABELS: Record<Ability, string> = {
  strength: 'STR',
  dexterity: 'DEX',
  constitution: 'CON',
  intelligence: 'INT',
  wisdom: 'WIS',
  charisma: 'CHA',
};

// Quick presets
const PRESETS = [
  {
    id: 'warrior',
    name: 'Warrior',
    description: 'Strength-based fighter',
    race: 'Human',
    className: 'Fighter',
    abilities: { strength: 15, dexterity: 12, constitution: 14, intelligence: 8, wisdom: 13, charisma: 10 },
  },
  {
    id: 'mage',
    name: 'Mage',
    description: 'Intelligence-based wizard',
    race: 'Elf',
    className: 'Wizard',
    abilities: { strength: 8, dexterity: 14, constitution: 12, intelligence: 15, wisdom: 13, charisma: 10 },
  },
  {
    id: 'healer',
    name: 'Healer',
    description: 'Wisdom-based cleric',
    race: 'Dwarf',
    className: 'Cleric',
    abilities: { strength: 13, dexterity: 10, constitution: 14, intelligence: 8, wisdom: 15, charisma: 12 },
  },
  {
    id: 'sneak',
    name: 'Sneak',
    description: 'Dexterity-based rogue',
    race: 'Halfling',
    className: 'Rogue',
    abilities: { strength: 8, dexterity: 15, constitution: 12, intelligence: 13, wisdom: 10, charisma: 14 },
  },
];

// =============================================================================
// WIZARD STEPS
// =============================================================================

const STEPS = ['basics', 'class', 'abilities', 'combat', 'review'] as const;
type Step = typeof STEPS[number];

const STEP_TITLES: Record<Step, string> = {
  basics: 'Basic Info',
  class: 'Class',
  abilities: 'Ability Scores',
  combat: 'Combat Stats',
  review: 'Review',
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CharacterCreationWizard({ onClose, onCreated }: CharacterCreationWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);

  // Get campaign profile for filtering
  const getCurrentCampaign = useCampaignStore((s) => s.getCurrentCampaign);
  const currentCampaign = getCurrentCampaign();
  const campaignProfile = currentCampaign?.settings?.profile;
  const techLevel = campaignProfile?.setting?.tech_level || 'medieval';
  const magicLevel = campaignProfile?.setting?.magic_level || 'medium';

  // Filter available races and classes based on campaign
  const availableRaces = getAvailableRaces(techLevel, magicLevel);
  const availableClasses = getAvailableClasses(techLevel, magicLevel);

  // Find default class (Fighter if available, otherwise first)
  const defaultClass = availableClasses.find(c => c.name === 'Fighter') || availableClasses[0];

  // Form state
  const [name, setName] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [race, setRace] = useState(availableRaces[0]?.name || 'Human');
  const [selectedClass, setSelectedClass] = useState<ClassData>(defaultClass);
  const [level, setLevel] = useState(1);
  const [subclass, setSubclass] = useState('');
  const [abilities, setAbilities] = useState<AbilityScores>({
    strength: 10,
    dexterity: 10,
    constitution: 10,
    intelligence: 10,
    wisdom: 10,
    charisma: 10,
  });
  const [armorClass, setArmorClass] = useState(10);
  const [maxHp, setMaxHp] = useState(10);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Dice rolling state for ability scores
  const [abilityMethod, setAbilityMethod] = useState<'standard' | 'rolled'>('standard');
  // Rolled pool: array of { dice: number[], total: number } for assignment
  const [rolledPool, setRolledPool] = useState<{ dice: number[]; total: number }[]>([]);
  const [rerollsRemaining, setRerollsRemaining] = useState(3);
  const [isRolling, setIsRolling] = useState(false);

  // Dice store access
  const diceRoll = useDiceStore((s) => s.roll);
  const isDiceReady = useDiceStore((s) => s.isReady);

  // Store access
  const addCharacter = useCharacterStore((s) => s.addCharacter);
  const addToParty = useCharacterStore((s) => s.addToParty);

  // Calculate proficiency bonus based on level
  const proficiencyBonus = Math.ceil(1 + level / 4);

  // Calculate suggested HP (max at level 1, average thereafter)
  const calculateSuggestedHp = useCallback(() => {
    const conMod = getModifier(abilities.constitution);
    const hitDie = selectedClass.hitDie;
    // Level 1: max hit die + CON mod
    // Levels 2+: average + CON mod per level
    const avgRoll = Math.ceil(hitDie / 2) + 1;
    return hitDie + conMod + (level - 1) * (avgRoll + conMod);
  }, [abilities.constitution, selectedClass.hitDie, level]);

  // Calculate suggested AC (10 + DEX mod)
  const calculateSuggestedAc = useCallback(() => {
    return 10 + getModifier(abilities.dexterity);
  }, [abilities.dexterity]);

  // Generate 4d6 drop lowest (pure random, no animation)
  const generate4d6DropLowest = useCallback((): { dice: number[]; total: number } => {
    const dice = Array.from({ length: 4 }, () => Math.floor(Math.random() * 6) + 1);
    dice.sort((a, b) => b - a);
    const total = dice[0] + dice[1] + dice[2];
    return { dice, total };
  }, []);

  // Roll all ability scores - generates a pool of 6 values to assign
  const rollAllAbilities = useCallback(async () => {
    if (isRolling) return;
    setIsRolling(true);
    setAbilityMethod('rolled');

    // Roll 4d6 once with the 3D dice for drama
    if (isDiceReady) {
      await diceRoll('4d6');
    }

    // Generate all 6 stat rolls
    const pool: { dice: number[]; total: number }[] = [];
    for (let i = 0; i < 6; i++) {
      pool.push(generate4d6DropLowest());
    }
    // Sort by total descending for easier assignment
    pool.sort((a, b) => b.total - a.total);

    setRolledPool(pool);
    // Reset abilities to defaults - player will assign from pool
    setAbilities({
      strength: 10,
      dexterity: 10,
      constitution: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10,
    });
    setSelectedPreset(null);
    setIsRolling(false);
  }, [isRolling, isDiceReady, diceRoll, generate4d6DropLowest]);

  // Reroll all abilities
  const rerollAbilities = useCallback(async () => {
    if (rerollsRemaining <= 0) return;
    setRerollsRemaining(prev => prev - 1);
    await rollAllAbilities();
  }, [rerollsRemaining, rollAllAbilities]);

  // Switch back to standard array
  const useStandardArray = useCallback(() => {
    setAbilityMethod('standard');
    setAbilities({
      strength: 10,
      dexterity: 10,
      constitution: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10,
    });
    setRolledPool([]);
    setRerollsRemaining(3);
    setSelectedPreset(null);
  }, []);

  // Apply preset (only if race/class are available in this campaign)
  const applyPreset = useCallback((preset: typeof PRESETS[0]) => {
    // Check if race is available
    const raceAvailable = availableRaces.some(r => r.name === preset.race);
    if (raceAvailable) {
      setRace(preset.race);
    }
    // Check if class is available
    const classData = availableClasses.find(c => c.name === preset.className);
    if (classData) {
      setSelectedClass(classData);
    }
    setAbilities(preset.abilities);
    setSelectedPreset(preset.id);
  }, [availableRaces, availableClasses]);

  // Navigation
  const nextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      // Auto-calculate combat stats when entering combat step
      if (STEPS[currentStep + 1] === 'combat') {
        setMaxHp(calculateSuggestedHp());
        setArmorClass(calculateSuggestedAc());
      }
      setCurrentStep(s => s + 1);
    }
  }, [currentStep, calculateSuggestedHp, calculateSuggestedAc]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  }, [currentStep]);

  // Submit
  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Character name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create the character
      const character = addCharacter({
        name: name.trim(),
        playerName: playerName.trim() || undefined,
        race,
        classes: [{
          name: selectedClass.name,
          level,
          subclass: subclass.trim() || undefined,
          hitDie: selectedClass.hitDie,
        }],
        abilityScores: abilities,
        proficiencyBonus,
        armorClass,
        maxHp,
        currentHp: maxHp,
        hitDice: `${level}d${selectedClass.hitDie}`,
        hitDiceRemaining: level,
        savingThrowProficiencies: selectedClass.saves,
      });

      // Add to party
      addToParty(character.id);

      onCreated?.(character.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create character');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step content
  const renderStepContent = () => {
    const step = STEPS[currentStep];

    switch (step) {
      case 'basics':
        // Filter presets to only show those with available race+class
        const validPresets = PRESETS.filter(p =>
          availableRaces.some(r => r.name === p.race) &&
          availableClasses.some(c => c.name === p.className)
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Import Option */}
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              style={{
                padding: '10px 14px',
                background: 'rgba(30, 25, 40, 0.8)',
                border: '1px dashed rgba(212, 175, 55, 0.4)',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Icon name="plus" size={14} color="rgba(255,255,255,0.6)" />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                Import existing character
              </span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                (JSON, PDF, or image)
              </span>
            </button>

            {/* Campaign Setting Info */}
            {(techLevel !== 'medieval' || magicLevel !== 'medium') && (
              <div style={{
                padding: 10,
                background: 'rgba(100, 80, 140, 0.15)',
                border: '1px solid rgba(100, 80, 140, 0.3)',
                borderRadius: 4,
                fontSize: 11,
                color: 'rgba(255,255,255,0.7)',
              }}>
                <strong style={{ color: '#a78bfa' }}>Campaign Setting:</strong>{' '}
                {techLevel.charAt(0).toUpperCase() + techLevel.slice(1)} era,{' '}
                {magicLevel === 'low' ? 'low magic' : magicLevel === 'wild' ? 'wild magic' : 'standard magic'}
                <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                  Some races/classes may be unavailable based on campaign settings.
                </div>
              </div>
            )}

            {/* Quick Presets */}
            {validPresets.length > 0 && (
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: GOLD }}>
                  Quick Presets
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {validPresets.map(preset => {
                    const isSelected = selectedPreset === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        style={{
                          padding: '10px 12px',
                          background: isSelected
                            ? 'rgba(212, 175, 55, 0.15)'
                            : 'rgba(30, 25, 40, 0.8)',
                          border: isSelected
                            ? `2px solid ${GOLD}`
                            : '1px solid rgba(212, 175, 55, 0.3)',
                          borderRadius: 4,
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}>
                          {isSelected && <Icon name="check" size={14} color={GOLD} />}
                          <span style={{
                            fontSize: 13,
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? GOLD : '#fff',
                          }}>
                            {preset.name}
                          </span>
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: isSelected ? 'rgba(212, 175, 55, 0.8)' : 'rgba(255,255,255,0.6)',
                          marginTop: 2,
                          marginLeft: isSelected ? 20 : 0,
                        }}>
                          {preset.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Show what the selected preset applied */}
                {selectedPreset && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    background: 'rgba(212, 175, 55, 0.1)',
                    border: `1px solid ${GOLD_DIM}`,
                    borderRadius: 4,
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.8)',
                  }}>
                    <Icon name="check" size={12} color="#4ade80" style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Applied: <strong style={{ color: GOLD }}>{race} {selectedClass.name}</strong>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}> • Ability scores set</span>
                  </div>
                )}
              </div>
            )}

            {/* Character Name */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Character Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter character name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            {/* Player Name */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Player Name
              </label>
              <input
                type="text"
                value={playerName}
                onChange={e => setPlayerName(e.target.value)}
                placeholder="Who plays this character?"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            {/* Race */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Race
              </label>
              <select
                value={race}
                onChange={e => setRace(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                {availableRaces.map(r => (
                  <option key={r.name} value={r.name}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'class':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Class Selection Grid */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: GOLD }}>
                Class
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {availableClasses.map(cls => (
                  <button
                    key={cls.name}
                    type="button"
                    onClick={() => setSelectedClass(cls)}
                    style={{
                      padding: '12px 8px',
                      background: selectedClass.name === cls.name
                        ? 'rgba(212, 175, 55, 0.2)'
                        : 'rgba(30, 25, 40, 0.8)',
                      border: selectedClass.name === cls.name
                        ? `2px solid ${GOLD}`
                        : '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Icon
                      name={cls.icon}
                      size={20}
                      color={selectedClass.name === cls.name ? GOLD : 'rgba(255,255,255,0.7)'}
                    />
                    <span style={{
                      fontSize: 11,
                      color: selectedClass.name === cls.name ? GOLD : 'rgba(255,255,255,0.8)',
                      fontWeight: selectedClass.name === cls.name ? 600 : 400,
                    }}>
                      {cls.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Level */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Level
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={level}
                  onChange={e => setLevel(parseInt(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{
                  width: 32,
                  textAlign: 'center',
                  fontSize: 18,
                  fontWeight: 600,
                  color: GOLD,
                }}>
                  {level}
                </span>
              </div>
            </div>

            {/* Subclass (optional) */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Subclass (optional)
              </label>
              <input
                type="text"
                value={subclass}
                onChange={e => setSubclass(e.target.value)}
                placeholder="e.g., Champion, Evocation, Circle of the Moon"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            {/* Class Info */}
            <div style={{
              padding: 12,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
            }}>
              <div>Hit Die: <strong style={{ color: GOLD }}>d{selectedClass.hitDie}</strong></div>
              <div>Saving Throws: <strong style={{ color: GOLD }}>{selectedClass.saves.join(', ')}</strong></div>
              <div>Proficiency Bonus: <strong style={{ color: GOLD }}>+{proficiencyBonus}</strong></div>
            </div>
          </div>
        );

      case 'abilities': {
        // Get the values available for assignment based on method
        const availableValues = abilityMethod === 'standard'
          ? STANDARD_ARRAY
          : rolledPool.map(r => r.total);

        // Track which values are already assigned
        const assignedValues = Object.values(abilities).filter(v => v !== 10);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Method Toggle */}
            <div style={{
              display: 'flex',
              gap: 8,
            }}>
              <button
                type="button"
                onClick={useStandardArray}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: abilityMethod === 'standard'
                    ? 'rgba(212, 175, 55, 0.2)'
                    : 'rgba(30, 25, 40, 0.8)',
                  border: abilityMethod === 'standard'
                    ? `2px solid ${GOLD}`
                    : '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: abilityMethod === 'standard' ? GOLD : 'rgba(255,255,255,0.8)',
                }}>
                  Standard Array
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  15, 14, 13, 12, 10, 8
                </div>
              </button>
              <button
                type="button"
                onClick={rollAllAbilities}
                disabled={isRolling}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: abilityMethod === 'rolled'
                    ? 'rgba(147, 51, 234, 0.2)'
                    : 'rgba(30, 25, 40, 0.8)',
                  border: abilityMethod === 'rolled'
                    ? '2px solid #a855f7'
                    : '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  cursor: isRolling ? 'wait' : 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: abilityMethod === 'rolled' ? '#a855f7' : 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}>
                  <Icon name="d20" size={14} color={abilityMethod === 'rolled' ? '#a855f7' : 'rgba(255,255,255,0.6)'} />
                  {isRolling ? 'Rolling...' : 'Roll Stats'}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  4d6 drop lowest
                </div>
              </button>
            </div>

            {/* Rolled Pool Display */}
            {abilityMethod === 'rolled' && rolledPool.length > 0 && !isRolling && (
              <div style={{
                padding: '10px 12px',
                background: 'rgba(147, 51, 234, 0.1)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                borderRadius: 4,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Your Rolled Stats
                  </div>
                  <button
                    type="button"
                    onClick={rerollAbilities}
                    disabled={rerollsRemaining <= 0 || isRolling}
                    style={{
                      padding: '4px 10px',
                      background: rerollsRemaining > 0 ? 'rgba(147, 51, 234, 0.3)' : 'rgba(50, 40, 60, 0.5)',
                      border: '1px solid rgba(147, 51, 234, 0.5)',
                      borderRadius: 4,
                      color: rerollsRemaining > 0 ? '#c084fc' : 'rgba(255,255,255,0.4)',
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: rerollsRemaining > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Icon name="d20" size={10} />
                    Reroll ({rerollsRemaining})
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {rolledPool.map((roll, idx) => {
                    // Count how many times this value appears in the pool up to (and including) this index
                    const countInPoolUpToIdx = rolledPool.slice(0, idx + 1).filter(r => r.total === roll.total).length;
                    // Count how many abilities have been assigned this value
                    const countAssigned = assignedValues.filter(v => v === roll.total).length;
                    // This specific roll chip is "used" only if enough assignments exist to cover it
                    const isUsed = countInPoolUpToIdx <= countAssigned;
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: '6px 10px',
                          background: isUsed ? 'rgba(74, 222, 128, 0.15)' : 'rgba(30, 25, 40, 0.8)',
                          border: `1px solid ${isUsed ? 'rgba(74, 222, 128, 0.4)' : 'rgba(147, 51, 234, 0.4)'}`,
                          borderRadius: 4,
                          opacity: isUsed ? 0.6 : 1,
                        }}
                      >
                        {/* Mini dice display */}
                        <div style={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                          {roll.dice.map((d, i) => (
                            <span
                              key={i}
                              style={{
                                fontSize: 8,
                                color: i === 3 ? '#ef4444' : 'rgba(255,255,255,0.5)',
                                textDecoration: i === 3 ? 'line-through' : 'none',
                              }}
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                        <div style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: isUsed ? '#4ade80' : '#c084fc',
                          textAlign: 'center',
                        }}>
                          {roll.total}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ability Score Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {ABILITIES.map(ability => {
                // For dropdowns, show available values plus current value plus unassigned placeholder
                const currentValue = abilities[ability];
                const dropdownOptions = [10, ...availableValues].filter((val) => {
                  // Include 10 (unassigned), current value, and unused values
                  if (val === 10) return true;
                  if (val === currentValue) return true;
                  // Count how many times this value appears in availableValues
                  const availableCount = availableValues.filter(v => v === val).length;
                  // Count how many times it's assigned (excluding current ability)
                  const assignedCount = ABILITIES.filter(a => a !== ability && abilities[a] === val).length;
                  return assignedCount < availableCount;
                });
                // Remove duplicates and sort
                const uniqueOptions = [...new Set(dropdownOptions)].sort((a, b) => b - a);

                return (
                  <div key={ability}>
                    <label style={{
                      display: 'block',
                      marginBottom: 4,
                      fontSize: 11,
                      color: GOLD,
                      fontWeight: 500,
                    }}>
                      {ABILITY_LABELS[ability]}
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <select
                        value={abilities[ability]}
                        onChange={e => setAbilities(prev => ({
                          ...prev,
                          [ability]: parseInt(e.target.value),
                        }))}
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          background: 'rgba(30, 25, 40, 0.8)',
                          border: `1px solid ${abilityMethod === 'rolled' ? 'rgba(147, 51, 234, 0.3)' : 'rgba(212, 175, 55, 0.3)'}`,
                          borderRadius: 4,
                          color: abilities[ability] === 10 ? 'rgba(255,255,255,0.4)' : '#fff',
                          fontSize: 14,
                        }}
                      >
                        {uniqueOptions.map(val => (
                          <option key={val} value={val}>
                            {val === 10 ? '—' : val}
                          </option>
                        ))}
                      </select>
                      <span style={{
                        width: 36,
                        textAlign: 'center',
                        fontSize: 13,
                        fontWeight: 500,
                        color: abilities[ability] === 10
                          ? 'rgba(255,255,255,0.3)'
                          : getModifier(abilities[ability]) >= 0
                            ? 'rgba(134, 239, 172, 0.9)'
                            : 'rgba(248, 113, 113, 0.9)',
                      }}>
                        {abilities[ability] === 10 ? '—' : (
                          (getModifier(abilities[ability]) >= 0 ? '+' : '') + getModifier(abilities[ability])
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Assignment progress */}
            <div style={{
              padding: 10,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
              color: 'rgba(255,255,255,0.6)',
              textAlign: 'center',
            }}>
              {assignedValues.length < 6 ? (
                <span>
                  Assigned: <strong style={{ color: GOLD }}>{assignedValues.length}/6</strong>
                  <span style={{ marginLeft: 8, fontSize: 11 }}>
                    (select a value for each ability)
                  </span>
                </span>
              ) : (
                <span>
                  Total: <strong style={{ color: GOLD }}>
                    {Object.values(abilities).reduce((sum, val) => sum + val, 0)}
                  </strong>
                  <Icon name="check" size={14} color="#4ade80" style={{ marginLeft: 8, verticalAlign: 'middle' }} />
                </span>
              )}
            </div>
          </div>
        );
      }

      case 'combat':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* HP */}
            <div>
              <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: GOLD }}>
                <span>Max Hit Points</span>
                <button
                  type="button"
                  onClick={() => setMaxHp(calculateSuggestedHp())}
                  style={{
                    padding: '2px 8px',
                    background: 'rgba(212, 175, 55, 0.2)',
                    border: `1px solid ${GOLD_DIM}`,
                    borderRadius: 3,
                    color: GOLD,
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  Suggest: {calculateSuggestedHp()}
                </button>
              </label>
              <input
                type="number"
                min={1}
                value={maxHp}
                onChange={e => setMaxHp(parseInt(e.target.value) || 1)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                Based on d{selectedClass.hitDie} + CON modifier ({getModifier(abilities.constitution) >= 0 ? '+' : ''}{getModifier(abilities.constitution)})
              </div>
            </div>

            {/* AC */}
            <div>
              <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: GOLD }}>
                <span>Armor Class</span>
                <button
                  type="button"
                  onClick={() => setArmorClass(calculateSuggestedAc())}
                  style={{
                    padding: '2px 8px',
                    background: 'rgba(212, 175, 55, 0.2)',
                    border: `1px solid ${GOLD_DIM}`,
                    borderRadius: 3,
                    color: GOLD,
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  Suggest: {calculateSuggestedAc()}
                </button>
              </label>
              <input
                type="number"
                min={1}
                value={armorClass}
                onChange={e => setArmorClass(parseInt(e.target.value) || 10)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                Unarmored: 10 + DEX modifier. Adjust for armor/shield.
              </div>
            </div>

            {/* Speed (informational) */}
            <div style={{
              padding: 12,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
            }}>
              <div>Speed: <strong style={{ color: GOLD }}>30 ft.</strong></div>
              <div>Hit Dice: <strong style={{ color: GOLD }}>{level}d{selectedClass.hitDie}</strong></div>
            </div>
          </div>
        );

      case 'review':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Character Summary */}
            <div style={{
              padding: 16,
              background: 'rgba(20, 15, 30, 0.8)',
              borderRadius: 4,
              border: `1px solid ${GOLD_DIM}`,
            }}>
              <h3 style={{ fontSize: 18, marginBottom: 8, color: GOLD }}>{name || 'Unnamed Character'}</h3>
              {playerName && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                  Played by {playerName}
                </div>
              )}
              <div style={{ fontSize: 14, marginBottom: 4 }}>
                {race} {selectedClass.name} {level}
                {subclass && <span style={{ color: 'rgba(255,255,255,0.6)' }}> ({subclass})</span>}
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}>
              <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: GOLD, marginBottom: 2 }}>HP</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{maxHp}</div>
              </div>
              <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: GOLD, marginBottom: 2 }}>AC</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#60a5fa' }}>{armorClass}</div>
              </div>
              <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: GOLD, marginBottom: 2 }}>PROF</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#a78bfa' }}>+{proficiencyBonus}</div>
              </div>
            </div>

            {/* Abilities */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 4,
            }}>
              {ABILITIES.map(ability => (
                <div key={ability} style={{
                  padding: 6,
                  background: 'rgba(30, 25, 40, 0.6)',
                  borderRadius: 4,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 9, color: GOLD }}>{ABILITY_LABELS[ability]}</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{abilities[ability]}</div>
                  <div style={{
                    fontSize: 10,
                    color: getModifier(abilities[ability]) >= 0 ? '#86efac' : '#f87171',
                  }}>
                    {getModifier(abilities[ability]) >= 0 ? '+' : ''}{getModifier(abilities[ability])}
                  </div>
                </div>
              ))}
            </div>

            {/* Saves */}
            <div style={{
              padding: 10,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
            }}>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>Saving Throw Proficiencies: </span>
              <span style={{ color: GOLD }}>{selectedClass.saves.join(', ')}</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 440,
          maxHeight: '85vh',
          background: 'linear-gradient(180deg, #1a1520 0%, #0f0d12 100%)',
          border: `1px solid ${GOLD_DIM}`,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${GOLD_DIM}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ fontSize: 16, margin: 0, color: GOLD }}>Create Character</h2>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
              Step {currentStep + 1} of {STEPS.length}: {STEP_TITLES[STEPS[currentStep]]}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px' }}>
          {STEPS.map((_, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: index <= currentStep ? GOLD : 'rgba(255,255,255,0.1)',
                transition: 'background 0.3s',
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '8px 20px 20px',
          overflowY: 'auto',
        }}>
          {renderStepContent()}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '0 20px 12px',
            padding: 10,
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 4,
            fontSize: 12,
            color: '#ef4444',
          }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: `1px solid ${GOLD_DIM}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <button
            type="button"
            onClick={currentStep === 0 ? onClose : prevStep}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid rgba(255,255,255,0.2)`,
              borderRadius: 4,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={currentStep === 0 && !name.trim()}
              style={{
                padding: '8px 20px',
                background: GOLD,
                border: 'none',
                borderRadius: 4,
                color: '#000',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: currentStep === 0 && !name.trim() ? 0.5 : 1,
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
              style={{
                padding: '8px 20px',
                background: '#22c55e',
                border: 'none',
                borderRadius: 4,
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: isSubmitting || !name.trim() ? 0.5 : 1,
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Character'}
            </button>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <CharacterImportModal
          onClose={() => setShowImportModal(false)}
          onImported={(characterId) => {
            setShowImportModal(false);
            onCreated?.(characterId);
            onClose();
          }}
        />
      )}
    </div>
  );
}
